import { passwordRules, onboarding } from '@portal/config/app.json';
import { keepOnlyAlphaNumeric } from './format';
import { validateCNPJ, validateCPF } from 'validations-br';

import * as MessageService from '~/services/message';

export interface PasswordValidation {
  min: boolean;
  max: boolean;
  number?: boolean;
  uppercase?: boolean;
  lowercase?: boolean;
  special?: boolean;
  letter?: boolean;
}

export const checkPasswordValidation = (
  passwordValidation: PasswordValidation
) => Object.values(passwordValidation).every((field) => field);

export const validatePassword = (value: string) => {
  const defaultPasswordValidation: PasswordValidation = {
    min: false,
    max: false,
  };

  const validate = Object.assign({}, defaultPasswordValidation);
  validate.min = value.length >= passwordRules.minLength;
  validate.max =
    value.length >= passwordRules.minLength &&
    value.length <= passwordRules.maxLength;

  if (passwordRules.atLeastOneNumber) validate.number = /\d/g.test(value);
  if (passwordRules.upperCase) validate.uppercase = /[A-Z]/g.test(value);
  if (passwordRules.lowerCase) validate.lowercase = /[a-z]/g.test(value);
  if (passwordRules.specialCharacter)
    validate.special = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/g.test(value);
  if (passwordRules.atLeastOneLetter) validate.letter = /[a-zA-Z]/g.test(value);

  return validate;
};

export const isValidHour = (value: number) => {
  const isValidHour = value !== null || value !== undefined;

  return isValidHour;
};

export const validateSmsCode = (code: string) => {
  if (code.length > 6) {
    code = code.substring(-1, code.length - 1);
  }

  code = keepOnlyAlphaNumeric(code.toUpperCase());
  return code;
};

export const validateCpfOrCnpj = (value: string) => {
  if (value) {
    const checkedValue = value.replace(/[^\d]+/g, '');

    if (
      checkedValue.length < 11 ||
      checkedValue.length === 13 ||
      checkedValue.length === 12 ||
      checkedValue.length > 14
    ) {
      return true;
    }

    if (checkedValue.length === 11 && !validateCPF(checkedValue)) {
      return true;
    }

    if (checkedValue.length === 14 && !validateCNPJ(checkedValue)) {
      return true;
    }
  }
};

export const containsOnlyLettersAndSpaces = (value: string) => {
  const pattern = /^[a-zA-Z\s]+$/;

  return pattern.test(value);
};

export const validateEmail = (email: string) => {
  const pattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  return pattern.test(email);
};

export const checkFileMaxSize = (
  file: React.ChangeEvent<HTMLInputElement>,
  maxFileSizeMB: number
) => {
  const fileInput = file.target;
  const maxFileSize = maxFileSizeMB * 1024 * 1024;

  if (fileInput.files && fileInput.files.length > 0) {
    const fileSize = fileInput.files[0].size;

    return fileSize > maxFileSize;
  }

  return true;
};

export const validateOnboardingDocument = (
  file: React.ChangeEvent<HTMLInputElement>
) => {
  const isMaxSizeValidationActive =
    onboarding.maxFileSizeImage.enabled || onboarding.maxFileSizePdf.enabled;
  if (isMaxSizeValidationActive) {
    const fileType = file.currentTarget.type;

    const maxSizeInMb = fileType.startsWith('image/')
      ? onboarding.maxFileSizeImage.sizeInMb
      : onboarding.maxFileSizePdf.sizeInMb;
    if (checkFileMaxSize(file, maxSizeInMb)) {
      MessageService.error('MAX_FILE_SIZE');
      return false;
    }
  }
  return true;
};

export const hasSequence = (str: string) => {
  const regex = /(.)\1{2,}/;
  return regex.test(str);
};

export const validateAge = (
  dateToValidate: string,
  ageToValidade: number,
  validation?: 'older' | 'newer'
) => {
  const date = new Date(dateToValidate);
  const actualDate = new Date();

  let actualAge = actualDate.getFullYear() - date.getFullYear();

  const actualMonth = actualDate.getMonth();
  const actualDay = actualDate.getDate();

  const monthDate = date.getMonth();
  const monthDay = date.getDate();

  if (
    actualMonth < monthDate ||
    (actualMonth === monthDate && actualDay < monthDay)
  ) {
    actualAge--;
  }

  if (validation === 'newer') {
    if (actualAge < ageToValidade) {
      return true;
    } else {
      return false;
    }
  }

  if (actualAge > ageToValidade) {
    return true;
  } else {
    return false;
  }
};
